import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarClock, Gavel, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import { Seccion, useSecciones } from "@/components/shell/Seccion";
import { fmtFechaCorta, fmtMXN, fmtPct, nuevoId, r2 } from "@/lib/operativo";
import {
  METODOS_INTERES_REV,
  PERIODICIDADES_CORTE,
  type ConfigRevolvente,
  type DisposicionRev,
  type MetodoInteresRev,
  type PagoRev,
  type PenaRev,
  type PeriodicidadCorte,
} from "@/lib/revolvente";
import { useOperativo } from "@/lib/operativo-store";
import { EstadoCuentaRevolvente, BadgeEstatusRev } from "./EstadoCuentaRevolvente";

type SeccionId = "linea" | "tasas" | "minimo" | "comisiones";

export function LineaRevolvente() {
  const {
    configRev: cfg,
    setConfigRev,
    disposiciones,
    setDisposiciones,
    pagosRev,
    setPagosRev,
    penasRev,
    setPenasRev,
    hoyRev,
    setHoyRev,
    estadoRev: e,
    cargarDemoRevolvente,
    limpiarRevolvente,
    ejecutarCorte,
    cargos,
  } = useOperativo();

  const secs = useSecciones<SeccionId>(["linea", "tasas", "minimo", "comisiones"]);
  const set = (patch: Partial<ConfigRevolvente>) => setConfigRev({ ...cfg, ...patch });

  /** Copia las comisiones de la plantilla del producto revolvente hacia la línea. */
  const restaurarComisionesDelProducto = () => {
    const propios = cargos.filter((c) => c.productoId === cfg.productoId && c.aplicar !== false);
    const buscar = (clave: string) =>
      propios.find((c) => `${c.nombre} ${c.categoria}`.toLowerCase().includes(clave));
    const disposicion = buscar("disposición") ?? buscar("apertura");
    const anualidad = buscar("anualidad");
    set({
      comisionDisposicionPct:
        disposicion?.tipoImporte === "%" ? Number(disposicion.importe) || 0 : 0,
      ivaComisionDisposicion: disposicion ? disposicion.causaIva : cfg.ivaComisionDisposicion,
      comisionAnualidad: anualidad?.tipoImporte === "$" ? Number(anualidad.importe) || 0 : 0,
      ivaAnualidad: anualidad ? anualidad.causaIva : cfg.ivaAnualidad,
    });
  };

  const [disp, setDisp] = useState<Omit<DisposicionRev, "id">>({
    fecha: cfg.fechaApertura,
    monto: 0,
    concepto: "",
  });
  const [errDisp, setErrDisp] = useState<string | null>(null);
  const [pago, setPago] = useState<Omit<PagoRev, "id">>({
    fecha: hoyRev,
    monto: 0,
    referencia: "",
  });
  const [errPago, setErrPago] = useState<string | null>(null);
  const [pena, setPena] = useState<Omit<PenaRev, "id">>({
    fecha: hoyRev,
    importe: 500,
    causaIva: true,
    referencia: "",
  });

  const agregarDisp = () => {
    setErrDisp(null);
    if (!disp.fecha || !(disp.monto > 0)) {
      setErrDisp("Captura una fecha válida y un monto mayor a $0.00.");
      return;
    }
    if (disp.monto > e.lineaDisponible + 0.004) {
      setErrDisp(`El monto excede la línea disponible (${fmtMXN(e.lineaDisponible)}).`);
      return;
    }
    if (e.bloqueado) {
      setErrDisp("La línea está bloqueada por cartera vencida (switch de bloqueo activo).");
      return;
    }
    setDisposiciones([...disposiciones, { ...disp, id: nuevoId("disp") }]);
    setDisp({ ...disp, monto: 0, concepto: "" });
  };

  const agregarPago = () => {
    setErrPago(null);
    if (!pago.fecha || !(pago.monto > 0)) {
      setErrPago("Captura una fecha válida y un monto mayor a $0.00.");
      return;
    }
    setPagosRev([...pagosRev, { ...pago, id: nuevoId("pagorev") }]);
    setPago({ ...pago, monto: 0, referencia: "" });
  };

  const num = (v: number) => (Number.isNaN(v) ? 0 : v);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
            Línea revolvente (crédito revolvente)
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Línea de crédito con revolvencia: cada pago a capital libera línea de inmediato. Corte
            periódico con interés sobre saldo promedio diario o saldo al corte, pago mínimo
            configurable, mora sobre el pago mínimo no cubierto y la misma prelación de 7 niveles
            (IVA pena → pena → IVA mora → mora → IVA interés → interés → capital).
          </p>
        </div>
        <div className="no-print grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button size="sm" onClick={cargarDemoRevolvente}>
            <Sparkles className="mr-1.5 h-4 w-4" /> Cargar ejemplo revolvente
          </Button>
          <Button size="sm" variant="outline" onClick={limpiarRevolvente}>
            <RotateCcw className="mr-1.5 h-4 w-4" /> Limpiar línea
          </Button>
        </div>
      </div>

      {/* KPIs de línea */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { l: "Línea autorizada", v: fmtMXN(cfg.lineaAutorizada), c: "text-navy" },
          { l: "Línea disponible", v: fmtMXN(e.lineaDisponible), c: "text-emerald" },
          { l: "Saldo de capital", v: fmtMXN(e.saldoCapital), c: "text-navy" },
          { l: "Pago mínimo pendiente", v: fmtMXN(e.minimoPendiente), c: "text-chart-3" },
        ].map((k) => (
          <Card key={k.l} className="border-navy/15 p-4 shadow-sm">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.l}</p>
            <p className={`num text-lg font-semibold ${k.c}`}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* Configuración de la línea */}
      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-navy" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Configuración de la línea
            </h3>
          </div>
          <Badge variant="secondary" className="num">
            Producto Meta Revolvente · {fmtPct(cfg.tasaAnual, 0)} anual
          </Badge>
        </div>

        <div className="space-y-3">
          <Seccion
            titulo="Línea y ciclo de corte"
            abierta={secs.abierta("linea")}
            onToggle={() => secs.toggle("linea")}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rev-linea">Línea de crédito autorizada</Label>
                <Input
                  id="rev-linea"
                  type="number"
                  inputMode="decimal"
                  className="num"
                  value={Number.isNaN(cfg.lineaAutorizada) ? "" : cfg.lineaAutorizada}
                  onChange={(ev) => set({ lineaAutorizada: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-apertura">Fecha de apertura de la línea</Label>
                <Input
                  id="rev-apertura"
                  type="date"
                  value={cfg.fechaApertura}
                  onChange={(ev) => set({ fechaApertura: ev.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Periodicidad de corte</Label>
                <Select
                  value={cfg.periodicidadCorte}
                  onValueChange={(v) => set({ periodicidadCorte: v as PeriodicidadCorte })}
                >
                  <SelectTrigger id="rev-periodicidad">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODICIDADES_CORTE.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-diacorte">Día de corte del mes (1–28)</Label>
                <Input
                  id="rev-diacorte"
                  type="number"
                  inputMode="decimal"
                  min={1}
                  max={28}
                  className="num"
                  value={Number.isNaN(cfg.diaCorte) ? "" : cfg.diaCorte}
                  onChange={(ev) =>
                    set({ diaCorte: Math.min(28, Math.max(1, num(ev.target.valueAsNumber))) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-diaspagar">Días para pagar después del corte</Label>
                <Input
                  id="rev-diaspagar"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  className="num"
                  value={Number.isNaN(cfg.diasParaPagar) ? "" : cfg.diasParaPagar}
                  onChange={(ev) => set({ diasParaPagar: num(ev.target.valueAsNumber) })}
                />
                <p className="text-[11px] text-muted-foreground">
                  Fecha límite del último corte:{" "}
                  <span className="num">{fmtFechaCorta(e.ultimoCerrado?.fechaLimite)}</span>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-impagos">Número de impagos para cartera vencida</Label>
                <Input
                  id="rev-impagos"
                  type="number"
                  inputMode="decimal"
                  min={1}
                  className="num"
                  value={Number.isNaN(cfg.numImpagos) ? "" : cfg.numImpagos}
                  onChange={(ev) => set({ numImpagos: num(ev.target.valueAsNumber) })}
                />
              </div>
            </div>
          </Seccion>

          <Seccion
            titulo="Tasas e intereses"
            abierta={secs.abierta("tasas")}
            onToggle={() => secs.toggle("tasas")}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rev-tasa">Tasa de interés anual (%)</Label>
                <Input
                  id="rev-tasa"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  className="num"
                  value={Number.isNaN(cfg.tasaAnual) ? "" : cfg.tasaAnual}
                  onChange={(ev) => set({ tasaAnual: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-mora">Tasa moratoria anual (%)</Label>
                <Input
                  id="rev-mora"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  className="num"
                  value={Number.isNaN(cfg.tasaMoratoria) ? "" : cfg.tasaMoratoria}
                  onChange={(ev) => set({ tasaMoratoria: num(ev.target.valueAsNumber) })}
                />
                {r2(cfg.tasaMoratoria) !== r2(cfg.tasaAnual * 2) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-1"
                    onClick={() => set({ tasaMoratoria: r2(cfg.tasaAnual * 2) })}
                  >
                    Recalcular a 2× ({fmtPct(cfg.tasaAnual * 2, 0)})
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Método de cálculo de interés</Label>
                <Select
                  value={cfg.metodoInteres}
                  onValueChange={(v) => set({ metodoInteres: v as MetodoInteresRev })}
                >
                  <SelectTrigger id="rev-metodo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METODOS_INTERES_REV.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {cfg.metodoInteres === "Saldo promedio diario"
                    ? "Interés = saldo promedio diario × (tasa anual / 360) × días del período."
                    : "Interés = saldo insoluto al corte × tasa del período."}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-gracia">Días de gracia de mora</Label>
                <Input
                  id="rev-gracia"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  className="num"
                  value={Number.isNaN(cfg.diasGraciaMora) ? "" : cfg.diasGraciaMora}
                  onChange={(ev) => set({ diasGraciaMora: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
                <Label htmlFor="rev-ivaint" className="text-xs">
                  ¿Causa IVA el interés?
                </Label>
                <Switch
                  id="rev-ivaint"
                  checked={cfg.ivaInteres}
                  onCheckedChange={(v) => set({ ivaInteres: v })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
                <Label htmlFor="rev-ivamora" className="text-xs">
                  ¿Causa IVA la mora?
                </Label>
                <Switch
                  id="rev-ivamora"
                  checked={cfg.ivaMora}
                  onCheckedChange={(v) => set({ ivaMora: v })}
                />
              </div>
            </div>
          </Seccion>

          <Seccion
            titulo="Pago mínimo y cobranza"
            abierta={secs.abierta("minimo")}
            onToggle={() => secs.toggle("minimo")}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rev-pct">% del saldo al corte</Label>
                <Input
                  id="rev-pct"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  className="num"
                  value={Number.isNaN(cfg.pctPagoMinimo) ? "" : cfg.pctPagoMinimo}
                  onChange={(ev) => set({ pctPagoMinimo: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-fijo">Pago mínimo fijo (piso)</Label>
                <Input
                  id="rev-fijo"
                  type="number"
                  inputMode="decimal"
                  className="num"
                  value={Number.isNaN(cfg.pagoMinimoFijo) ? "" : cfg.pagoMinimoFijo}
                  onChange={(ev) => set({ pagoMinimoFijo: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
                <Label htmlFor="rev-bloqueo" className="text-xs">
                  Bloquear disposiciones en mora
                </Label>
                <Switch
                  id="rev-bloqueo"
                  checked={cfg.bloquearDisposicionesEnMora}
                  onCheckedChange={(v) => set({ bloquearDisposicionesEnMora: v })}
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Pago mínimo = máx({fmtPct(cfg.pctPagoMinimo)} × saldo al corte + intereses del período
              + IVA + comisiones del período, {fmtMXN(cfg.pagoMinimoFijo)}). Si el saldo total es
              menor al mínimo, el pago mínimo es el saldo total.
            </p>
          </Seccion>

          <Seccion
            titulo="Comisiones"
            abierta={secs.abierta("comisiones")}
            onToggle={() => secs.toggle("comisiones")}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rev-comdisp">Comisión por disposición (%)</Label>
                <Input
                  id="rev-comdisp"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  className="num"
                  value={Number.isNaN(cfg.comisionDisposicionPct) ? "" : cfg.comisionDisposicionPct}
                  onChange={(ev) => set({ comisionDisposicionPct: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
                <Label htmlFor="rev-ivacomdisp" className="text-xs">
                  Comisión por disposición + IVA
                </Label>
                <Switch
                  id="rev-ivacomdisp"
                  checked={cfg.ivaComisionDisposicion}
                  onCheckedChange={(v) => set({ ivaComisionDisposicion: v })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rev-anualidad">Comisión por anualidad ($)</Label>
                <Input
                  id="rev-anualidad"
                  type="number"
                  inputMode="decimal"
                  className="num"
                  value={Number.isNaN(cfg.comisionAnualidad) ? "" : cfg.comisionAnualidad}
                  onChange={(ev) => set({ comisionAnualidad: num(ev.target.valueAsNumber) })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
                <Label htmlFor="rev-ivaanual" className="text-xs">
                  Anualidad + IVA
                </Label>
                <Switch
                  id="rev-ivaanual"
                  checked={cfg.ivaAnualidad}
                  onCheckedChange={(v) => set({ ivaAnualidad: v })}
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Las comisiones se cargan al saldo de la línea en la fecha del evento (disposición) o
              en el corte de aniversario (anualidad) y forman parte del pago mínimo del período.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="outline"
                className="no-print"
                onClick={restaurarComisionesDelProducto}
              >
                Restaurar del producto
              </Button>
              <p className="text-xs text-muted-foreground">
                Las comisiones de la línea son una copia editable de la plantilla del producto
                revolvente; editarlas aquí no modifica el producto.
              </p>
            </div>
          </Seccion>
        </div>
      </Card>

      {/* Línea de tiempo simulada */}
      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-chart-3" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Ciclo de corte y línea de tiempo simulada
          </h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="rev-hoy">Fecha actual simulada</Label>
            <Input
              id="rev-hoy"
              type="date"
              value={hoyRev}
              onChange={(ev) => setHoyRev(ev.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Próximo corte</Label>
            <p className="num pt-2 text-sm font-semibold text-navy">
              {fmtFechaCorta(e.proximoCorte)}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Cortes ejecutados</Label>
            <p className="num pt-2 text-sm font-semibold text-navy">{e.periodos.length}</p>
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={ejecutarCorte}>
              <CalendarClock className="mr-1.5 h-4 w-4" /> Ejecutar corte
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <BadgeEstatusRev estatus={e.estatus} />
          {e.bloqueado && (
            <Badge className="bg-destructive text-primary-foreground">
              Disposiciones bloqueadas por cartera vencida
            </Badge>
          )}
        </div>
      </Card>

      {/* Disposiciones */}
      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-emerald" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Registrar disposición
          </h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="d-fecha">Fecha</Label>
            <Input
              id="d-fecha"
              type="date"
              value={disp.fecha}
              onChange={(ev) => setDisp({ ...disp, fecha: ev.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-monto">Monto</Label>
            <Input
              id="d-monto"
              type="number"
              inputMode="decimal"
              className="num"
              value={Number.isNaN(disp.monto) ? "" : disp.monto}
              onChange={(ev) => setDisp({ ...disp, monto: num(ev.target.valueAsNumber) })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-concepto">Concepto / referencia</Label>
            <Input
              id="d-concepto"
              value={disp.concepto}
              onChange={(ev) => setDisp({ ...disp, concepto: ev.target.value.slice(0, 60) })}
            />
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={agregarDisp}>
              <Plus className="mr-1.5 h-4 w-4" /> Disponer
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Línea disponible = línea autorizada − saldo de capital vigente. Disponible ahora:{" "}
          <span className="num text-emerald">{fmtMXN(e.lineaDisponible)}</span>
        </p>
        {errDisp && <p className="mt-2 text-xs text-destructive">{errDisp}</p>}

        <div className="mt-4 space-y-2">
          {disposiciones.length === 0 && (
            <p className="text-xs text-muted-foreground">Sin disposiciones registradas.</p>
          )}
          {[...disposiciones]
            .sort((a, b) => a.fecha.localeCompare(b.fecha))
            .map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-navy/10 bg-surface px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="num text-sm font-medium text-navy">
                    {fmtFechaCorta(d.fecha)} · {fmtMXN(d.monto)}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {d.concepto || "Disposición de línea"}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Eliminar disposición"
                  onClick={() => setDisposiciones(disposiciones.filter((x) => x.id !== d.id))}
                  className="grid h-11 w-11 place-items-center text-navy/60 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
        </div>
      </Card>

      {/* Pagos */}
      <Card className="print-block border-navy/15 p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="h-4 w-1 rounded-full bg-navy" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
            Registrar pago de la línea
          </h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="pr-fecha">Fecha</Label>
            <Input
              id="pr-fecha"
              type="date"
              value={pago.fecha}
              onChange={(ev) => setPago({ ...pago, fecha: ev.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pr-monto">Monto</Label>
            <Input
              id="pr-monto"
              type="number"
              inputMode="decimal"
              className="num"
              value={Number.isNaN(pago.monto) ? "" : pago.monto}
              onChange={(ev) => setPago({ ...pago, monto: num(ev.target.valueAsNumber) })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pr-ref">Referencia</Label>
            <Input
              id="pr-ref"
              value={pago.referencia}
              onChange={(ev) => setPago({ ...pago, referencia: ev.target.value.slice(0, 60) })}
            />
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={agregarPago}>
              <Plus className="mr-1.5 h-4 w-4" /> Aplicar pago
            </Button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>
            Pago mínimo pendiente:{" "}
            <span className="num text-chart-3">{fmtMXN(e.minimoPendiente)}</span>
          </span>
          <span>
            Total a liquidar hoy:{" "}
            <span className="num text-navy">{fmtMXN(e.totalLiquidarHoy)}</span>
          </span>
          <button
            type="button"
            className="text-navy underline"
            onClick={() => setPago({ ...pago, monto: e.minimoPendiente })}
          >
            Usar pago mínimo
          </button>
          <button
            type="button"
            className="text-navy underline"
            onClick={() => setPago({ ...pago, monto: e.totalLiquidarHoy })}
          >
            Usar liquidación total
          </button>
        </div>
        {errPago && <p className="mt-2 text-xs text-destructive">{errPago}</p>}

        <div className="mt-4 space-y-2">
          {pagosRev.length === 0 && (
            <p className="text-xs text-muted-foreground">Sin pagos registrados.</p>
          )}
          {[...pagosRev]
            .sort((a, b) => a.fecha.localeCompare(b.fecha))
            .map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-navy/10 bg-surface px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="num text-sm font-medium text-emerald">
                    {fmtFechaCorta(p.fecha)} · {fmtMXN(p.monto)}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {p.referencia || "Pago recibido"}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Eliminar pago"
                  onClick={() => setPagosRev(pagosRev.filter((x) => x.id !== p.id))}
                  className="grid h-11 w-11 place-items-center text-navy/60 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
        </div>

        {/* Pena convencional manual */}
        <div className="mt-6 border-t pt-4">
          <div className="mb-3 flex items-center gap-2">
            <Gavel className="h-4 w-4 text-destructive" />
            <h4 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Registrar pena convencional (captura manual)
            </h4>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="pn-fecha">Fecha</Label>
              <Input
                id="pn-fecha"
                type="date"
                value={pena.fecha}
                onChange={(ev) => setPena({ ...pena, fecha: ev.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pn-importe">Importe</Label>
              <Input
                id="pn-importe"
                type="number"
                inputMode="decimal"
                className="num"
                value={Number.isNaN(pena.importe) ? "" : pena.importe}
                onChange={(ev) => setPena({ ...pena, importe: num(ev.target.valueAsNumber) })}
              />
            </div>
            <div className="flex items-center justify-between rounded-md bg-surface px-3 py-2.5">
              <Label htmlFor="pn-iva" className="text-xs">
                Causa IVA
              </Label>
              <Switch
                id="pn-iva"
                checked={pena.causaIva}
                onCheckedChange={(v) => setPena({ ...pena, causaIva: v })}
              />
            </div>
            <div className="flex items-end">
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => {
                  if (pena.fecha && pena.importe > 0)
                    setPenasRev([...penasRev, { ...pena, id: nuevoId("penarev") }]);
                }}
              >
                <Plus className="mr-1.5 h-4 w-4" /> Registrar pena
              </Button>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {penasRev.map((x) => (
              <div
                key={x.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-destructive/20 bg-surface px-3 py-2"
              >
                <p className="num text-sm text-destructive">
                  {fmtFechaCorta(x.fecha)} · {fmtMXN(x.importe)}
                  {x.causaIva ? " + IVA" : ""}
                </p>
                <button
                  type="button"
                  aria-label="Eliminar pena"
                  onClick={() => setPenasRev(penasRev.filter((y) => y.id !== x.id))}
                  className="grid h-11 w-11 place-items-center text-navy/60 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <EstadoCuentaRevolvente />
    </div>
  );
}
